import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Building2, Phone, Mail, GripVertical, Plus, Trash2, Save, X, Copy, Star, CheckCircle2, UserX, TrendingUp, CalendarCheck, Send, FileText } from 'lucide-react';
import { API } from '../../App';

const TABS = [
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'today', label: 'Today' },
  { id: 'past', label: 'Past' },
  { id: 'cancelled', label: 'Cancelled' },
];

const CANCEL_REASON_LABELS = {
  customer: 'Cancelled by Customer',
  admin_no_show: 'Cancelled by Boutique (No Show)',
};

const CAMPAIGN_EMAIL_LABELS = {
  f0_winback: 'Win-back email',
  trial_dupatta: 'Ambika Royal Trails email',
};

const formatSentDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SentEmailBadges = ({ appt }) => {
  const sent = appt.stage_emails_sent || {};
  const keys = Object.keys(sent);
  if (keys.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 mt-1" data-testid={`sent-emails-${appt.id}`}>
      {keys.map((k) => (
        <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-green-100 text-green-700 rounded" data-testid={`sent-email-${k}-${appt.id}`}>
          <Mail size={10} /> {CAMPAIGN_EMAIL_LABELS[k] || k} sent · {formatSentDate(sent[k])}
        </span>
      ))}
    </div>
  );
};

const SalesmanInput = ({ appt, onSave }) => {
  const [val, setVal] = useState(appt.dealt_by || '');
  useEffect(() => { setVal(appt.dealt_by || ''); }, [appt.dealt_by]);
  return (
    <input
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if ((val || '') !== (appt.dealt_by || '')) onSave(val.trim()); }}
      placeholder="Deal by (salesman)"
      className="border border-border px-2 py-1 text-xs w-full"
      data-testid={`dealt-by-${appt.id}`}
    />
  );
};

const AdminAppointmentsPage = ({ adminToken }) => {
  const [view, setView] = useState('list'); // list | pipeline | settings
  const [tab, setTab] = useState('upcoming');
  const [expandedForm, setExpandedForm] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [pipelineAppointments, setPipelineAppointments] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [activePipelineId, setActivePipelineId] = useState('');
  const [config, setConfig] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [report, setReport] = useState(null);
  const [uploadingHero, setUploadingHero] = useState('');
  const [formB2B, setFormB2B] = useState({ fields: [] });
  const [formB2C, setFormB2C] = useState({ fields: [] });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchAll = async () => {
    try {
      const [apRes, plRes, cfRes, b2bRes, b2cRes, allRes, cmpRes] = await Promise.all([
        axios.get(`${API}/admin/appointments?tab=${tab}`, { headers }),
        axios.get(`${API}/admin/appointments/pipelines`, { headers }),
        axios.get(`${API}/admin/appointments/config`, { headers }),
        axios.get(`${API}/admin/appointments/forms/B2B`, { headers }),
        axios.get(`${API}/admin/appointments/forms/B2C`, { headers }),
        axios.get(`${API}/admin/appointments?tab=all`, { headers }),
        axios.get(`${API}/admin/appointments/campaigns`, { headers }),
      ]);
      setAppointments(apRes.data || []);
      setPipelineAppointments(allRes.data || []);
      setPipelines(plRes.data || []);
      setConfig(cfRes.data || {});
      setCampaigns(cmpRes.data || []);
      setFormB2B(b2bRes.data || { fields: [] });
      setFormB2C(b2cRes.data || { fields: [] });
      if (!activePipelineId && plRes.data?.length) setActivePipelineId(plRes.data[0].id);
    } catch (e) {
      toast.error('Failed to load appointments');
    }
  };

  useEffect(() => { fetchAll(); }, [tab]);

  const fetchReport = async () => {
    try {
      const r = await axios.get(`${API}/admin/appointments/campaign-report`, { headers });
      setReport(r.data);
    } catch (e) {
      toast.error('Failed to load campaign report');
    }
  };
  useEffect(() => { if (view === 'report') fetchReport(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAppointment = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/appointments/${id}`, patch, { headers });
      toast.success('Updated');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Delete this appointment permanently? The slot capacity will be freed.')) return;
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { headers });
      toast.success('Appointment deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const checkInAppointment = async (id) => {
    try {
      await axios.post(`${API}/admin/appointments/${id}/check-in`, {}, { headers });
      toast.success('Customer marked as arrived');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to check in');
    }
  };

  const noShowAppointment = async (id) => {
    if (!window.confirm('Mark this customer as a no-show? The appointment will move to Cancelled.')) return;
    try {
      await axios.post(`${API}/admin/appointments/${id}/no-show`, {}, { headers });
      toast.success('Marked as no-show (cancelled by Boutique)');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark no-show');
    }
  };

  // Active pipeline object + its stages
  const activePipeline = useMemo(
    () => pipelines.find(p => p.id === activePipelineId) || pipelines[0] || null,
    [pipelines, activePipelineId]
  );
  const activeStages = activePipeline?.stages || [];

  // Group appointments in the active pipeline by stage.
  // Uses pipelineAppointments (tab=all) so ALL non-cancelled appointments show,
  // regardless of which List tab (Upcoming/Today/Past) is active.
  const grouped = useMemo(() => {
    const g = {};
    (activeStages || []).forEach(s => (g[s.name] = []));
    (pipelineAppointments || []).forEach(a => {
      // Only show appointments in the currently viewed pipeline
      if ((a.pipeline_id || null) !== (activePipeline?.id || null) && activePipeline) return;
      const stage = a.pipeline_stage || (activeStages[0]?.name) || 'New';
      if (!g[stage]) g[stage] = [];
      g[stage].push(a);
    });
    return g;
  }, [pipelineAppointments, activeStages, activePipeline]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/appointments/config`, config, { headers });
      toast.success('Configuration saved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const saveForm = async (type) => {
    const form = type === 'B2B' ? formB2B : formB2C;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/appointments/forms/${type}`, { fields: form.fields }, { headers });
      toast.success(`${type} form saved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  // --- Pipelines CRUD ---
  const createPipeline = async ({ copy_from = null } = {}) => {
    const name = window.prompt(copy_from ? 'Name for the duplicated pipeline:' : 'New pipeline name:');
    if (!name) return;
    try {
      const body = { name };
      if (copy_from) body.copy_from = copy_from;
      const res = await axios.post(`${API}/admin/appointments/pipelines`, body, { headers });
      toast.success('Pipeline created');
      setActivePipelineId(res.data.id);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
  };

  const updatePipeline = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/appointments/pipelines/${id}`, patch, { headers });
      toast.success('Pipeline updated');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const deletePipeline = async (id) => {
    if (!window.confirm('Delete this pipeline? Its appointments will be moved to another pipeline.')) return;
    try {
      await axios.delete(`${API}/admin/appointments/pipelines/${id}`, { headers });
      toast.success('Pipeline deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const savePipelineStages = async (id, stages) => {
    try {
      await axios.patch(`${API}/admin/appointments/pipelines/${id}`, { stages }, { headers });
      toast.success('Stages saved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const setCommon = (key, patch) => {
    setConfig({
      ...config,
      common_fields: {
        ...(config.common_fields || {}),
        [key]: { ...(config.common_fields?.[key] || {}), ...patch },
      },
    });
  };
  const setModes = (nextModes) => setConfig({ ...config, mode_options: nextModes });

  const setCampaignHero = (key, url) => {
    setConfig({
      ...config,
      campaign_hero_images: { ...(config.campaign_hero_images || {}), [key]: url },
    });
  };

  const uploadCampaignHero = async (key, file) => {
    if (!file) return;
    setUploadingHero(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/upload-image`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setCampaignHero(key, res.data.url);
      toast.success('Image uploaded — click Save to apply');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingHero('');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-appointments-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h1 className="font-heading text-3xl">Appointments</h1>
        <div className="flex gap-2">
          {['list', 'pipeline', 'report', 'settings'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-2 border text-sm capitalize ${view === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}
              data-testid={`view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      {view === 'list' && (
        <>
          <div className="flex gap-2 mb-4 border-b border-border">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? 'border-primary text-primary font-medium' : 'border-transparent hover:text-primary'}`}
                data-testid={`tab-${t.id}`}>{t.label}</button>
            ))}
          </div>
          {appointments.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">No appointments in this view.</p>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-accent/40 text-left">
                  <tr>
                    <th className="px-3 py-2">Client</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Contact</th>
                    <th className="px-3 py-2">Date / Time</th>
                    <th className="px-3 py-2">Mode</th>
                    <th className="px-3 py-2">Pipeline / Stage</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {appointments.map(a => {
                    const pl = pipelines.find(p => p.id === a.pipeline_id) || pipelines[0];
                    const modeLabel = (config?.mode_options || []).find(m => m.key === a.appointment_mode)?.label || a.appointment_mode;
                    const isExpanded = expandedForm === a.id;
                    return (
                      <React.Fragment key={a.id}>
                      <tr data-testid={`appt-row-${a.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{a.full_name}</div>
                          {a.form_responses?.['Company / Business Name'] && (
                            <div className="text-xs text-muted-foreground flex items-center gap-1"><Building2 size={11} /> {a.form_responses['Company / Business Name']}</div>
                          )}
                          {a.attendance === 'arrived' && a.status === 'active' && (
                            <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-green-100 text-green-700 rounded" data-testid={`arrived-badge-${a.id}`}>
                              <CheckCircle2 size={11} /> Arrived
                            </span>
                          )}
                          {a.status === 'cancelled' && (
                            <span className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded ${a.cancelled_reason === 'admin_no_show' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-testid={`cancel-reason-${a.id}`}>
                              {CANCEL_REASON_LABELS[a.cancelled_reason] || 'Cancelled'}
                            </span>
                          )}
                          <SentEmailBadges appt={a} />
                        </td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs bg-primary/10 text-primary">{a.client_type}</span></td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-1"><Mail size={11} />{a.email}</div>
                          <div className="flex items-center gap-1"><Phone size={11} />{a.phone}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1"><Calendar size={12} />{a.date}</div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={11} />{a.time_slot} IST</div>
                        </td>
                        <td className="px-3 py-2 text-xs">{modeLabel}</td>
                        <td className="px-3 py-2">
                          <select value={a.pipeline_id || pl?.id || ''}
                            onChange={(e) => updateAppointment(a.id, { pipeline_id: e.target.value })}
                            className="border border-border px-2 py-1 text-xs mr-1"
                            data-testid={`pipeline-select-${a.id}`}>
                            {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <select value={a.pipeline_stage || 'New'} onChange={(e) => updateAppointment(a.id, { pipeline_stage: e.target.value })}
                            className="border border-border px-2 py-1 text-xs" data-testid={`stage-select-${a.id}`}>
                            {(pl?.stages || []).map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex flex-col items-end gap-1">
                            {a.status === 'active' && a.attendance !== 'arrived' && (
                              <button onClick={() => checkInAppointment(a.id)}
                                className="text-green-700 hover:bg-green-50 px-2 py-1 border border-green-200 text-xs inline-flex items-center gap-1 w-full justify-center"
                                data-testid={`arrived-appt-${a.id}`}>
                                <CheckCircle2 size={12} /> Customer Arrived
                              </button>
                            )}
                            {a.status === 'active' && (
                              <button onClick={() => noShowAppointment(a.id)}
                                className="text-amber-700 hover:bg-amber-50 px-2 py-1 border border-amber-200 text-xs inline-flex items-center gap-1 w-full justify-center"
                                data-testid={`no-show-appt-${a.id}`}>
                                <UserX size={12} /> Customer Did Not Arrive
                              </button>
                            )}
                            {(tab === 'today' || tab === 'past') && (
                              <SalesmanInput appt={a} onSave={(v) => updateAppointment(a.id, { dealt_by: v })} />
                            )}
                            <button onClick={() => setExpandedForm(isExpanded ? null : a.id)}
                              className="text-primary hover:bg-primary/5 px-2 py-1 border border-primary/30 text-xs inline-flex items-center gap-1 w-full justify-center"
                              data-testid={`view-form-${a.id}`}>
                              <FileText size={12} /> {isExpanded ? 'Hide Form' : 'View Form'}
                            </button>
                            <button onClick={() => deleteAppointment(a.id)}
                              className="text-red-600 hover:bg-red-50 px-2 py-1 border border-red-200 text-xs inline-flex items-center gap-1 w-full justify-center"
                              data-testid={`delete-appt-${a.id}`}>
                              <Trash2 size={12} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr data-testid={`form-details-${a.id}`}>
                          <td colSpan={7} className="px-4 py-3 bg-accent/20">
                            <div className="text-xs font-medium mb-2 text-primary">Submitted Form Details</div>
                            {a.form_responses && Object.keys(a.form_responses).length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                                {Object.entries(a.form_responses).map(([k, v]) => (
                                  <div key={k} className="text-xs">
                                    <span className="text-muted-foreground">{k}: </span>
                                    <span className="font-medium">{String(v) || '—'}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">No additional form fields were submitted.</p>
                            )}
                            {a.dealt_by && (
                              <div className="text-xs mt-2"><span className="text-muted-foreground">Deal by (salesman): </span><span className="font-medium">{a.dealt_by}</span></div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === 'pipeline' && (
        <div data-testid="pipeline-board">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <label className="text-sm text-muted-foreground">Viewing pipeline:</label>
            <select value={activePipeline?.id || ''} onChange={(e) => setActivePipelineId(e.target.value)}
              className="border border-border px-3 py-2 text-sm" data-testid="pipeline-view-select">
              {pipelines.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_default_b2b ? ' · B2B default' : ''}{p.is_default_b2c ? ' · B2C default' : ''}
                </option>
              ))}
            </select>
            <button onClick={() => createPipeline()} className="text-sm border border-border px-3 py-2 flex items-center gap-1 hover:border-primary" data-testid="pipeline-new-btn">
              <Plus size={14} /> New Pipeline
            </button>
            {activePipeline && (
              <button onClick={() => createPipeline({ copy_from: activePipeline.id })} className="text-sm border border-border px-3 py-2 flex items-center gap-1 hover:border-primary" data-testid="pipeline-duplicate-btn">
                <Copy size={14} /> Duplicate
              </button>
            )}
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {activeStages.map(stage => (
              <div key={stage.id} className="min-w-[280px] flex-1 bg-accent/30 border border-border">
                <div className="p-3 border-b border-border flex items-center justify-between" style={{ borderTop: `3px solid ${stage.color}` }}>
                  <p className="font-medium text-sm">{stage.name}</p>
                  <span className="text-xs text-muted-foreground">{(grouped[stage.name] || []).length}</span>
                </div>
                <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto">
                  {(grouped[stage.name] || []).map(a => (
                    <div key={a.id} className="bg-white border border-border p-3 text-sm" data-testid={`kanban-card-${a.id}`}>
                      <p className="font-medium">{a.full_name}</p>
                      <p className="text-xs text-muted-foreground">{a.client_type} · {a.date} {a.time_slot}</p>
                      <p className="text-xs text-muted-foreground mb-2">
                        {(config?.mode_options || []).find(m => m.key === a.appointment_mode)?.label || a.appointment_mode}
                      </p>
                      <SentEmailBadges appt={a} />
                      <div className="space-y-1 mt-2">
                        <select value={a.pipeline_stage} onChange={(e) => updateAppointment(a.id, { pipeline_stage: e.target.value })} className="w-full text-xs border border-border px-2 py-1">
                          {activeStages.map(s => <option key={s.id} value={s.name}>Move to: {s.name}</option>)}
                        </select>
                        <select value={a.pipeline_id || ''} onChange={(e) => updateAppointment(a.id, { pipeline_id: e.target.value })} className="w-full text-xs border border-border px-2 py-1" data-testid={`kanban-move-pipeline-${a.id}`}>
                          {pipelines.map(p => <option key={p.id} value={p.id}>Pipeline: {p.name}</option>)}
                        </select>
                        <button onClick={() => deleteAppointment(a.id)} className="w-full text-xs text-red-600 border border-red-200 hover:bg-red-50 py-1 flex items-center justify-center gap-1" data-testid={`kanban-delete-${a.id}`}>
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {(grouped[stage.name] || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-4">—</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {view === 'report' && (
        <div data-testid="campaign-report">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h2 className="font-heading text-2xl">Campaign Email Report</h2>
              <p className="text-sm text-muted-foreground">Emails sent by stage &amp; how many led to a re-booking (same customer booked again after the email).</p>
            </div>
            <button onClick={fetchReport} className="text-sm border border-border px-3 py-2 hover:border-primary" data-testid="report-refresh">Refresh</button>
          </div>

          {!report ? (
            <p className="text-muted-foreground py-8 text-center">Loading report…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-white border border-border p-5" data-testid="report-total-sent">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><Send size={14} /> Emails Sent</div>
                  <p className="font-heading text-4xl mt-2">{report.total_sent}</p>
                </div>
                <div className="bg-white border border-border p-5" data-testid="report-total-rebooked">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><CalendarCheck size={14} /> Re-bookings</div>
                  <p className="font-heading text-4xl mt-2">{report.total_rebooked}</p>
                </div>
                <div className="bg-white border border-border p-5" data-testid="report-overall-rate">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><TrendingUp size={14} /> Conversion</div>
                  <p className="font-heading text-4xl mt-2">{report.overall_rate}%</p>
                </div>
              </div>

              <div className="border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-accent/40 text-left">
                    <tr>
                      <th className="px-4 py-3">Campaign</th>
                      <th className="px-4 py-3 text-right">Sent</th>
                      <th className="px-4 py-3 text-right">Re-booked</th>
                      <th className="px-4 py-3 text-right">Conversion</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(report.campaigns || []).map(c => (
                      <tr key={c.key} data-testid={`report-row-${c.key}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2"><Mail size={14} className="text-primary" /> {c.label}</div>
                        </td>
                        <td className="px-4 py-3 text-right" data-testid={`report-sent-${c.key}`}>{c.sent}</td>
                        <td className="px-4 py-3 text-right" data-testid={`report-rebooked-${c.key}`}>{c.rebooked}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`px-2 py-0.5 rounded text-xs ${c.rate >= 20 ? 'bg-green-100 text-green-700' : c.rate > 0 ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`} data-testid={`report-rate-${c.key}`}>{c.rate}%</span>
                        </td>
                      </tr>
                    ))}
                    {(report.campaigns || []).length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">No campaign data yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">A "re-booking" is counted when a customer who received a campaign email later creates another appointment (matched by email).</p>
            </>
          )}
        </div>
      )}

      {view === 'settings' && config && (
        <div className="space-y-6" data-testid="appointments-settings">
          {/* Availability config (unchanged) */}
          <section className="bg-white border border-border p-5">
            <h3 className="font-heading text-lg mb-4">Availability</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm mb-2">Open Days</label>
                <div className="flex gap-1 flex-wrap">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                    <button key={d} onClick={() => {
                      const set = new Set(config.open_days || []);
                      set.has(i) ? set.delete(i) : set.add(i);
                      setConfig({ ...config, open_days: Array.from(set).sort() });
                    }}
                      className={`px-3 py-2 border text-xs ${config.open_days?.includes(i) ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:border-primary'}`}
                      data-testid={`day-${d.toLowerCase()}`}>{d}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm mb-2">Slot Capacity</label>
                <input type="number" min={1} max={10} value={config.slot_capacity || 2}
                  onChange={(e) => setConfig({ ...config, slot_capacity: parseInt(e.target.value) || 1 })}
                  className="w-full border border-border px-3 py-2" data-testid="slot-capacity" />
              </div>
              <div>
                <label className="block text-sm mb-2">Booking Window (days ahead)</label>
                <input type="number" min={1} max={365} value={config.booking_window_days || 60}
                  onChange={(e) => setConfig({ ...config, booking_window_days: parseInt(e.target.value) || 60 })}
                  className="w-full border border-border px-3 py-2" data-testid="booking-window" />
              </div>
              <div>
                <label className="block text-sm mb-2">Notification Email</label>
                <input type="email" value={config.notification_email || ''} onChange={(e) => setConfig({ ...config, notification_email: e.target.value })}
                  className="w-full border border-border px-3 py-2" data-testid="notification-email" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-2">Time Slots (hourly grid — HH:00 only)</label>
              <div className="flex gap-2 flex-wrap">
                {(config.time_slots || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-1 border border-border px-3 py-1">
                    <span className="text-sm">{s}</span>
                    <button onClick={() => setConfig({ ...config, time_slots: config.time_slots.filter((_, j) => j !== i) })}
                      className="text-red-600 hover:bg-red-50 p-1"><X size={12} /></button>
                  </div>
                ))}
                <select onChange={(e) => {
                  if (!e.target.value) return;
                  const next = Array.from(new Set([...(config.time_slots || []), e.target.value])).sort();
                  setConfig({ ...config, time_slots: next });
                  e.target.value = '';
                }} className="border border-border px-2 py-1 text-sm" data-testid="add-slot-select">
                  <option value="">+ Add slot</option>
                  {Array.from({ length: 14 }, (_, h) => `${String(h + 8).padStart(2, '0')}:00`)
                    .filter(s => !(config.time_slots || []).includes(s))
                    .map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-2">Blocked Dates (holidays)</label>
              <div className="flex gap-2 flex-wrap">
                {(config.blocked_dates || []).map(d => (
                  <div key={d} className="flex items-center gap-1 border border-border px-3 py-1">
                    <span className="text-sm">{d}</span>
                    <button onClick={() => setConfig({ ...config, blocked_dates: config.blocked_dates.filter(x => x !== d) })}
                      className="text-red-600 hover:bg-red-50 p-1"><X size={12} /></button>
                  </div>
                ))}
                <input type="date" onChange={(e) => {
                  if (!e.target.value) return;
                  setConfig({ ...config, blocked_dates: Array.from(new Set([...(config.blocked_dates || []), e.target.value])).sort() });
                  e.target.value = '';
                }} className="border border-border px-2 py-1 text-sm" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm mb-2">Waiting-time Notice</label>
              <textarea rows={2} value={config.waiting_time_notice || ''}
                onChange={(e) => setConfig({ ...config, waiting_time_notice: e.target.value })}
                className="w-full border border-border px-3 py-2" />
            </div>
            <button onClick={saveConfig} disabled={saving} className="px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 disabled:opacity-50" data-testid="save-config-btn">
              <Save size={16} />{saving ? 'Saving…' : 'Save Availability'}
            </button>
          </section>

          {/* Common Fields editor with guardrails */}
          <section className="bg-white border border-border p-5" data-testid="common-fields-editor">
            <h3 className="font-heading text-lg mb-2">Common Booking Fields</h3>
            <p className="text-xs text-muted-foreground mb-4">Email, Phone, and Appointment Mode are required for confirmations and reminders — their labels can be edited but they cannot be hidden or made optional.</p>
            <div className="space-y-3">
              {['full_name', 'email', 'phone', 'appointment_mode'].map(key => {
                const f = config.common_fields?.[key] || {};
                const guardrailed = key !== 'full_name';
                return (
                  <div key={key} className="border border-border p-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{key.replace('_', ' ')}</p>
                    </div>
                    <input type="text" placeholder="Label" value={f.label || ''}
                      onChange={(e) => setCommon(key, { label: e.target.value })}
                      className="border border-border px-3 py-2 text-sm md:col-span-1"
                      data-testid={`common-label-${key}`} />
                    <div className="flex gap-4 items-center text-xs">
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={!!f.required} disabled={guardrailed}
                          onChange={(e) => setCommon(key, { required: e.target.checked })} /> Required
                      </label>
                      <label className="flex items-center gap-1">
                        <input type="checkbox" checked={!!f.hidden} disabled={guardrailed}
                          onChange={(e) => setCommon(key, { hidden: e.target.checked })} /> Hidden
                      </label>
                      {guardrailed && <span className="text-[10px] text-muted-foreground">(locked)</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={saveConfig} disabled={saving} className="mt-4 px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 disabled:opacity-50" data-testid="save-common-btn">
              <Save size={16} />{saving ? 'Saving…' : 'Save Common Fields'}
            </button>
          </section>

          {/* Mode Options editor */}
          <section className="bg-white border border-border p-5" data-testid="mode-options-editor">
            <h3 className="font-heading text-lg mb-2">Appointment Modes</h3>
            <p className="text-xs text-muted-foreground mb-4">Rename modes (e.g. &ldquo;Visit Showroom&rdquo;), edit the showroom address, add virtual options, or remove modes you don&rsquo;t use. At least one mode is required.</p>
            <div className="space-y-3">
              {(config.mode_options || []).map((m, i) => (
                <div key={i} className="border border-border p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Key</label>
                    <input type="text" value={m.key} onChange={(e) => {
                      const next = [...config.mode_options]; next[i] = { ...next[i], key: e.target.value }; setModes(next);
                    }} className="border border-border px-2 py-1 text-sm w-full" data-testid={`mode-key-${i}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Label</label>
                    <input type="text" value={m.label} onChange={(e) => {
                      const next = [...config.mode_options]; next[i] = { ...next[i], label: e.target.value }; setModes(next);
                    }} className="border border-border px-2 py-1 text-sm w-full" data-testid={`mode-label-${i}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Address (in-person only)</label>
                    <input type="text" value={m.address || ''} disabled={m.kind !== 'in-person'}
                      onChange={(e) => {
                        const next = [...config.mode_options]; next[i] = { ...next[i], address: e.target.value }; setModes(next);
                      }} className="border border-border px-2 py-1 text-sm w-full disabled:bg-muted/40" data-testid={`mode-address-${i}`} />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Google Maps link</label>
                    <input type="url" value={m.map_url || ''} disabled={m.kind !== 'in-person'} placeholder="https://maps.app.goo.gl/…"
                      onChange={(e) => {
                        const next = [...config.mode_options]; next[i] = { ...next[i], map_url: e.target.value }; setModes(next);
                      }} className="border border-border px-2 py-1 text-sm w-full disabled:bg-muted/40" data-testid={`mode-map-url-${i}`} />
                  </div>
                  <div className="flex items-end gap-2">
                    <select value={m.kind} onChange={(e) => {
                      const next = [...config.mode_options]; next[i] = { ...next[i], kind: e.target.value }; setModes(next);
                    }} className="border border-border px-2 py-1 text-sm flex-1" data-testid={`mode-kind-${i}`}>
                      <option value="in-person">In-person</option>
                      <option value="virtual">Virtual</option>
                    </select>
                    <button onClick={() => setModes(config.mode_options.filter((_, j) => j !== i))}
                      className="text-red-600 border border-red-200 hover:bg-red-50 p-2" data-testid={`mode-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => setModes([...(config.mode_options || []), { key: `mode-${Date.now()}`, label: 'New Mode', address: '', kind: 'in-person', map_url: '' }])}
                className="border border-border px-3 py-2 text-sm flex items-center gap-1 hover:border-primary" data-testid="mode-add-btn">
                <Plus size={14} /> Add Mode
              </button>
            </div>
            <button onClick={saveConfig} disabled={saving} className="mt-4 px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 disabled:opacity-50" data-testid="save-modes-btn">
              <Save size={16} />{saving ? 'Saving…' : 'Save Modes'}
            </button>
          </section>

          {/* Campaign Email Hero Images */}
          <section className="bg-white border border-border p-5" data-testid="campaign-hero-editor">
            <h3 className="font-heading text-lg mb-2">Campaign Email Hero Images</h3>
            <p className="text-xs text-muted-foreground mb-4">Set a distinct hero banner for each stage-triggered email (e.g. a dupatta banner for the trial email). If left empty, the email uses your homepage banner. Wide landscape images look best. Uploaded or a direct image URL both work.</p>
            <div className="space-y-4">
              {campaigns.map(c => {
                const url = (config.campaign_hero_images || {})[c.key] || '';
                return (
                  <div key={c.key} className="border border-border p-3 flex flex-col md:flex-row gap-4" data-testid={`campaign-hero-${c.key}`}>
                    <div className="w-full md:w-40 flex-shrink-0">
                      <div className="w-full h-24 bg-accent/40 border border-border overflow-hidden flex items-center justify-center">
                        {url
                          ? <img src={url} alt={c.label} className="w-full h-full object-cover" data-testid={`campaign-hero-preview-${c.key}`} />
                          : <span className="text-[10px] text-muted-foreground text-center px-2">Homepage banner (default)</span>}
                      </div>
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="font-medium text-sm">{c.label}</p>
                      <p className="text-xs text-muted-foreground">{c.stage_hint}</p>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="border border-border px-3 py-1.5 text-xs cursor-pointer hover:border-primary inline-flex items-center gap-1" data-testid={`campaign-hero-upload-${c.key}`}>
                          <Plus size={12} /> {uploadingHero === c.key ? 'Uploading…' : 'Upload Image'}
                          <input type="file" accept="image/*" className="hidden" disabled={uploadingHero === c.key}
                            onChange={(e) => { uploadCampaignHero(c.key, e.target.files?.[0]); e.target.value = ''; }} />
                        </label>
                        <input type="url" placeholder="…or paste an image URL" value={url}
                          onChange={(e) => setCampaignHero(c.key, e.target.value)}
                          className="flex-1 min-w-[180px] border border-border px-2 py-1.5 text-xs" data-testid={`campaign-hero-url-${c.key}`} />
                        {url && (
                          <button onClick={() => setCampaignHero(c.key, '')}
                            className="text-red-600 border border-red-200 hover:bg-red-50 px-2 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`campaign-hero-clear-${c.key}`}>
                            <X size={12} /> Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {campaigns.length === 0 && <p className="text-xs text-muted-foreground">No campaign emails configured.</p>}
            </div>
            <button onClick={saveConfig} disabled={saving} className="mt-4 px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 disabled:opacity-50" data-testid="save-campaign-hero-btn">
              <Save size={16} />{saving ? 'Saving…' : 'Save Campaign Images'}
            </button>
          </section>

          {/* Pipelines CRUD */}
          <section className="bg-white border border-border p-5" data-testid="pipelines-editor">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg">Pipelines</h3>
              <button onClick={() => createPipeline()} className="border border-border px-3 py-2 text-sm flex items-center gap-1 hover:border-primary" data-testid="pipelines-new-btn">
                <Plus size={14} /> New Pipeline
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Each pipeline has its own stages. Mark one as the B2B default and one as the B2C default — new appointments are auto-assigned by client type.</p>
            <div className="space-y-4">
              {pipelines.map(p => (
                <div key={p.id} className="border border-border p-4" data-testid={`pipeline-block-${p.id}`}>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <input type="text" value={p.name}
                      onBlur={(e) => e.target.value !== p.name && updatePipeline(p.id, { name: e.target.value })}
                      onChange={(e) => {
                        const nx = pipelines.map(x => x.id === p.id ? { ...x, name: e.target.value } : x);
                        setPipelines(nx);
                      }}
                      className="font-heading text-lg border-b border-transparent focus:border-primary outline-none px-1"
                      data-testid={`pipeline-name-${p.id}`} />
                    <div className="flex gap-2 flex-wrap">
                      <label className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!p.is_default_b2b} onChange={(e) => updatePipeline(p.id, { is_default_b2b: e.target.checked })} />
                        <Star size={12} /> B2B default
                      </label>
                      <label className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={!!p.is_default_b2c} onChange={(e) => updatePipeline(p.id, { is_default_b2c: e.target.checked })} />
                        <Star size={12} /> B2C default
                      </label>
                      <button onClick={() => createPipeline({ copy_from: p.id })} className="border border-border px-2 py-1 text-xs flex items-center gap-1 hover:border-primary" data-testid={`pipeline-copy-${p.id}`}>
                        <Copy size={12} /> Duplicate
                      </button>
                      <button onClick={() => deletePipeline(p.id)} className="border border-red-200 text-red-600 px-2 py-1 text-xs flex items-center gap-1 hover:bg-red-50" data-testid={`pipeline-delete-${p.id}`}>
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                  <PipelineStagesEditor pipeline={p} onSave={(stages) => savePipelineStages(p.id, stages)} />
                </div>
              ))}
            </div>
          </section>

          {/* Form builders (unchanged) */}
          {['B2B', 'B2C'].map(type => {
            const form = type === 'B2B' ? formB2B : formB2C;
            const setForm = type === 'B2B' ? setFormB2B : setFormB2C;
            return (
              <section key={type} className="bg-white border border-border p-5" data-testid={`form-builder-${type.toLowerCase()}`}>
                <h3 className="font-heading text-lg mb-4">{type} Custom Form Fields</h3>
                <div className="space-y-2 mb-4">
                  {(form.fields || []).map((f, i) => (
                    <div key={f.id || i} className="border border-border p-3 space-y-2">
                      <div className="flex gap-2 items-center flex-wrap">
                        <input type="text" placeholder="Field label" value={f.label} onChange={(e) => {
                          const nf = [...form.fields]; nf[i] = { ...nf[i], label: e.target.value }; setForm({ ...form, fields: nf });
                        }} className="flex-1 border border-border px-2 py-1 text-sm min-w-[180px]" />
                        <select value={f.field_type} onChange={(e) => {
                          const nf = [...form.fields]; nf[i] = { ...nf[i], field_type: e.target.value }; setForm({ ...form, fields: nf });
                        }} className="border border-border px-2 py-1 text-sm">
                          {['text', 'textarea', 'select', 'email', 'tel', 'checkbox'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <label className="flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={f.required} onChange={(e) => {
                            const nf = [...form.fields]; nf[i] = { ...nf[i], required: e.target.checked }; setForm({ ...form, fields: nf });
                          }} /> Required
                        </label>
                        <button onClick={() => { const n = [...form.fields]; if (i > 0) { [n[i - 1], n[i]] = [n[i], n[i - 1]]; setForm({ ...form, fields: n }); } }} className="p-1 border border-border text-xs">↑</button>
                        <button onClick={() => { const n = [...form.fields]; if (i < n.length - 1) { [n[i + 1], n[i]] = [n[i], n[i + 1]]; setForm({ ...form, fields: n }); } }} className="p-1 border border-border text-xs">↓</button>
                        <button onClick={() => setForm({ ...form, fields: form.fields.filter((_, j) => j !== i) })} className="text-red-600 p-1"><Trash2 size={14} /></button>
                      </div>
                      {f.field_type === 'select' && (
                        <div className="space-y-2" data-testid={`options-editor-${f.id || i}`}>
                          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Options</p>
                          {((f.options && f.options.length > 0) ? f.options : ['']).map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={opt}
                                placeholder={`Option ${oi + 1}`}
                                onChange={(e) => {
                                  const nf = [...form.fields];
                                  const current = nf[i].options && nf[i].options.length > 0 ? [...nf[i].options] : [''];
                                  current[oi] = e.target.value;
                                  nf[i] = { ...nf[i], options: current };
                                  setForm({ ...form, fields: nf });
                                }}
                                className="flex-1 border border-border px-2 py-1 text-sm"
                                data-testid={`option-input-${f.id || i}-${oi}`}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const nf = [...form.fields];
                                  const current = nf[i].options && nf[i].options.length > 0 ? [...nf[i].options] : [''];
                                  const next = current.filter((_, j) => j !== oi);
                                  nf[i] = { ...nf[i], options: next };
                                  setForm({ ...form, fields: nf });
                                }}
                                className="text-red-600 border border-red-200 hover:bg-red-50 p-1"
                                aria-label="Remove option"
                                data-testid={`option-remove-${f.id || i}-${oi}`}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const nf = [...form.fields];
                              const current = nf[i].options && nf[i].options.length > 0 ? [...nf[i].options] : [];
                              nf[i] = { ...nf[i], options: [...current, ''] };
                              setForm({ ...form, fields: nf });
                            }}
                            className="border border-border px-2 py-1 text-xs flex items-center gap-1 hover:border-primary"
                            data-testid={`option-add-${f.id || i}`}
                          >
                            <Plus size={12} /> Add Option
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setForm({ ...form, fields: [...(form.fields || []), { id: crypto.randomUUID(), label: 'New field', field_type: 'text', required: false, options: [] }] })}
                    className="px-3 py-2 border border-border flex items-center gap-1 text-sm" data-testid={`add-field-${type.toLowerCase()}`}><Plus size={14} /> Add Field</button>
                  <button onClick={() => saveForm(type)} disabled={saving} className="px-6 py-2 bg-primary text-primary-foreground flex items-center gap-2 disabled:opacity-50" data-testid={`save-form-${type.toLowerCase()}`}>
                    <Save size={16} />{saving ? 'Saving…' : `Save ${type} Form`}
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Inline sub-component: edit a pipeline's stages (rename/reorder/add/delete/color).
const PipelineStagesEditor = ({ pipeline, onSave }) => {
  const [stages, setStages] = useState(pipeline.stages || []);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setStages(pipeline.stages || []); setDirty(false); }, [pipeline.id, pipeline.stages]);

  const update = (idx, patch) => {
    setStages(prev => { const nx = [...prev]; nx[idx] = { ...nx[idx], ...patch }; return nx; });
    setDirty(true);
  };
  const move = (from, to) => {
    if (to < 0 || to >= stages.length) return;
    setStages(prev => { const nx = [...prev]; [nx[from], nx[to]] = [nx[to], nx[from]]; return nx; });
    setDirty(true);
  };
  const remove = (idx) => { setStages(prev => prev.filter((_, i) => i !== idx)); setDirty(true); };
  const add = () => { setStages(prev => [...prev, { id: crypto.randomUUID(), name: 'New Stage', order: prev.length + 1, color: '#6b7280' }]); setDirty(true); };

  return (
    <div>
      <div className="space-y-2 mb-3">
        {stages.map((s, i) => (
          <div key={s.id || i} className="flex items-center gap-2 border border-border p-2 bg-accent/20">
            <GripVertical size={14} className="text-muted-foreground" />
            <input type="text" value={s.name} onChange={(e) => update(i, { name: e.target.value })}
              className="flex-1 border border-border px-2 py-1 text-sm" data-testid={`stage-name-${pipeline.id}-${i}`} />
            <input type="color" value={s.color} onChange={(e) => update(i, { color: e.target.value })}
              className="w-10 h-8 border border-border" />
            <button onClick={() => move(i, i - 1)} className="p-1 border border-border text-xs">↑</button>
            <button onClick={() => move(i, i + 1)} className="p-1 border border-border text-xs">↓</button>
            <button onClick={() => remove(i)} className="text-red-600 p-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={add} className="px-3 py-2 border border-border flex items-center gap-1 text-xs" data-testid={`add-stage-${pipeline.id}`}>
          <Plus size={12} /> Add Stage
        </button>
        <button onClick={() => { onSave(stages); setDirty(false); }} disabled={!dirty}
          className="px-4 py-2 bg-primary text-primary-foreground flex items-center gap-1 text-xs disabled:opacity-40" data-testid={`save-stages-${pipeline.id}`}>
          <Save size={12} /> Save Stages
        </button>
      </div>
    </div>
  );
};

export default AdminAppointmentsPage;
